import { TranslateHelper } from '../../helpers/injector.helper';
import { ValueAndType } from '../basic/formulaEditor.model';
import { OperationType } from '../enums/operationtype.enum';
import { ValueType } from '../enums/valuetype.enum';
import { FormulaNodeCalculator } from './formula.node.calculator';
import { AFormula, FormulaGroups } from './iformula';

// FormatDate, ParseDate, TimeAdd -- the client side of GA/Base/Calculation/DatumsFormeln.cs.
// The format tokens are those of .NET that the workflows use: yyyy yy MM M dd d HH H mm m ss s fff,
// text in single quotes is literal.

const KUERZEL = /yyyy|yy|MM|M|dd|d|HH|H|mm|m|ss|s|fff|'[^']*'/g;

function zwei(n: number): string {
    return n < 10 ? '0' + n : '' + n;
}

export function datumFormatieren(datum: Date, format: string): string {
    if (!format) {
        return datum.toISOString();
    }
    return format.replace(KUERZEL, k => {
        switch (k) {
            case 'yyyy': return '' + datum.getFullYear();
            case 'yy': return zwei(datum.getFullYear() % 100);
            case 'MM': return zwei(datum.getMonth() + 1);
            case 'M': return '' + (datum.getMonth() + 1);
            case 'dd': return zwei(datum.getDate());
            case 'd': return '' + datum.getDate();
            case 'HH': return zwei(datum.getHours());
            case 'H': return '' + datum.getHours();
            case 'mm': return zwei(datum.getMinutes());
            case 'm': return '' + datum.getMinutes();
            case 'ss': return zwei(datum.getSeconds());
            case 's': return '' + datum.getSeconds();
            case 'fff': return ('00' + datum.getMilliseconds()).slice(-3);
            default: return k.slice(1, -1);
        }
    });
}

export function datumLesen(text: string, format: string): Date {
    if (text === null || text === undefined) {
        return null;
    }
    text = ('' + text).trim();
    if (!format) {
        const d = new Date(text);
        return isNaN(d.getTime()) ? null : d;
    }
    const teile: string[] = [];
    const muster = format.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/yyyy|yy|MM|M|dd|d|HH|H|mm|m|ss|s|fff|'[^']*'/g, k => {
        if (k.startsWith('\'')) {
            return k.slice(1, -1);
        }
        teile.push(k);
        return k === 'yyyy' ? '(\\d{4})' : k === 'fff' ? '(\\d{3})' : k.length === 2 ? '(\\d{2})' : '(\\d{1,2})';
    });
    const treffer = new RegExp('^' + muster + '$').exec(text);
    if (!treffer) {
        return null;
    }
    let j = 1, mo = 1, t = 1, h = 0, mi = 0, s = 0, ms = 0;
    teile.forEach((k, i) => {
        const n = Number(treffer[i + 1]);
        switch (k[0]) {
            case 'y': j = k === 'yy' ? 2000 + n : n; break;
            case 'M': mo = n; break;
            case 'd': t = n; break;
            case 'H': h = n; break;
            case 'm': mi = n; break;
            case 's': s = n; break;
            case 'f': ms = n; break;
        }
    });
    const d = new Date(j, mo - 1, t, h, mi, s, ms);
    // As in .NET: 31.02. is no date, not the 3rd of March.
    return d.getMonth() === mo - 1 && d.getDate() === t ? d : null;
}

abstract class ADatumsFormel extends AFormula {
    Group(): string {
        return FormulaGroups.Functions;
    }

    OperationType(): OperationType {
        return OperationType.Function;
    }

    protected t(schluessel: string): string {
        return TranslateHelper.TranslatorInstance.instant(schluessel);
    }

    protected static datum(wert: ValueAndType): Date {
        return wert && wert.Value instanceof Date && !isNaN(wert.Value.getTime()) ? wert.Value : null;
    }
}

export class FormatDate extends ADatumsFormel {
    static StaticID = 'ab834c2b-c0d0-40ef-bb4f-dc92956c770f';
    ID(): string { return FormatDate.StaticID; }
    Name(): string { return 'FormatDate'; }
    Description(): string { return this.t('@@Formatiert ein Datum als Text, z. B. dd.MM.yyyy HH:mm'); }
    OrderID(): number { return 102; }
    OperationValue(): string { return this.Name() + '(' + this.t('@@Datumswert') + '; ' + this.t('@@Format') + ')'; }
    MinVariableCount(): number { return 2; }
    MaxVariableCount(): number { return 2; }

    Calc(fnc: FormulaNodeCalculator, maxType: ValueType, args?: ValueAndType[]): ValueAndType {
        const datum = ADatumsFormel.datum(args && args[0]);
        return ValueAndType.GetValueAndTypeFromJSObject(datum ? datumFormatieren(datum, args[1] ? args[1].Value : null) : null);
    }
}

export class ParseDate extends ADatumsFormel {
    static StaticID = 'f33fb1e4-1792-47c1-b3b2-3df9d566399e';
    ID(): string { return ParseDate.StaticID; }
    Name(): string { return 'ParseDate'; }
    Description(): string { return this.t('@@Liest ein Datum aus einem Text, z. B. mit dd.MM.yyyy; ohne Format ISO 8601'); }
    OrderID(): number { return 103; }
    OperationValue(): string { return this.Name() + '(' + this.t('@@Text') + '; ' + this.t('@@Format') + ')'; }
    MinVariableCount(): number { return 1; }
    MaxVariableCount(): number { return 2; }

    Calc(fnc: FormulaNodeCalculator, maxType: ValueType, args?: ValueAndType[]): ValueAndType {
        const schon = ADatumsFormel.datum(args && args[0]);
        const datum = schon ?? datumLesen(args && args[0] ? args[0].Value : null, args && args.length > 1 ? args[1].Value : null);
        return ValueAndType.GetValueAndTypeFromJSObject(datum);
    }
}

export class TimeAdd extends ADatumsFormel {
    static StaticID = 'e1203547-4507-4863-b57e-80cde7b2835d';
    ID(): string { return TimeAdd.StaticID; }
    Name(): string { return 'TimeAdd'; }
    Description(): string { return this.t('@@Addiert Stunden, Minuten und Sekunden auf einen Datumswert'); }
    OrderID(): number { return 104; }
    OperationValue(): string {
        return this.Name() + '(' + this.t('@@Datumswert') + '; ' + this.t('@@Stunden') + '; ' + this.t('@@Minuten') + '; ' + this.t('@@Sekunden') + ')';
    }
    MinVariableCount(): number { return 4; }
    MaxVariableCount(): number { return 4; }

    Calc(fnc: FormulaNodeCalculator, maxType: ValueType, args?: ValueAndType[]): ValueAndType {
        const datum = ADatumsFormel.datum(args && args[0]);
        if (!datum) {
            return ValueAndType.GetValueAndTypeFromJSObject(null);
        }
        const zahl = (w: ValueAndType) => { const n = Number(w ? w.Value : 0); return isNaN(n) ? 0 : n; };
        const ms = ((zahl(args[1]) * 60 + zahl(args[2])) * 60 + zahl(args[3])) * 1000;
        return ValueAndType.GetValueAndTypeFromJSObject(new Date(datum.getTime() + ms));
    }
}
